import React, { useContext, useEffect, useState } from "react";
import { Text, CustomTable } from "react_reusable_components";
import {
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";

import TableActions from "../../components/TableActions";
import { recordsPerPageOptions, PAGE_LIMIT, userRole } from "../../constants";
import { columns } from "./ProductCategoryListingColumns";
import { TwoRow } from "../../core/layouts";
import ProductCategoryListingTopContainer from "../ProductCategoryListingTopContainer/ProductCategoryListingTopContainer";
import Styles from "./productCategoryListingContainer.module.scss";
import ProductCategoryService from "../../services/product-category-service";
import { ProductCategoryContext } from "../../globalContext/productCategory/productCategoryProvider";
import { useIntl } from "react-intl";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { responsiveArray } from "antd/es/_util/responsiveObserver";
import useResponsive from "../../core/hooks/useResponsive";
import SetCountryStatusModal from "../SetCountryStatusModalContainer/SetCountryStatusModalContainer";
import {
  setQueryInStore,
} from "../../globalContext/productCategory/productCategoryActions";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { showNotification } from "../../UtilityFunctions/notification";
import { AuthContext } from "../../globalContext/auth/authProvider";
import variables from "../../themes/base/styles/variables";

const ProductCategoryListingContainer = () => {
  const responsive = useResponsive();
  const [productCategoryState, productCategoryDispatch] = useContext(
    ProductCategoryContext
  );
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [query, setQuery] = useState({
    page: productCategoryState?.query?.page || 1,
    recordsPerPage: productCategoryState?.query?.recordsPerPage || PAGE_LIMIT,
    searchStr: productCategoryState?.query?.searchStr || "",
  });
  const [openModal, setOpenModal] = useState(false);
  const [activeRecord, setActiveRecord] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();
  const intl = useIntl();
  const [authState] = useContext(AuthContext);

  useEffect(() => {
    setSelectedRow(null);
  }, [productCategoryState?.productCategoryList?.result]);

  useEffect(() => {
    let queryParams = getProductCategoryListParams(query);
    productCategoryDispatch(setQueryInStore(query));
    const debouncedApiCall = debounce(() => {
      ProductCategoryService.getProductCategoryList(
        productCategoryDispatch,
        queryParams,
        intl,
        loaderDispatch
      );
    }, 400);

    debouncedApiCall();
  }, [query]);

  const tableActionsOptions = (record) =>
    [
      {
        label: "View details",
        icon: <EyeOutlined className={Styles["icon-style"]} />,
        // navigate to view details
        action: () =>
          navigate(`/view-product-category/${record.productCategoryId}`),
      },
      authState?.currentRole?.id === userRole.ADMIN && {
        label: "Edit details",
        icon: <EditOutlined className={Styles["icon-style"]} />,
        // navigate to edit details
        action: () =>
          navigate(`/edit-product-category/${record.productCategoryId}`),
      },
      {
        label: "Mark subcategory Active/Inactive for Country",
        icon: <CheckCircleOutlined className={Styles["icon-style"]} />,
        // navigate
        action: () => {
          setActiveRecord(record);
          setOpenModal(true);
        },
      },
    ].filter((item) => item);

  const downloadProductCategoryReportsHandler = () => {
    let queryParams = getProductCategoryListParams(query);

    delete queryParams.pageLimit;
    delete queryParams.pageSkip;

    if (productCategoryState?.productCategoryList?.result?.length)
      ProductCategoryService.getDownloadProductCategoryReports({
        params: queryParams,
        intl,
        loaderDispatch: loaderDispatch,
      });
    else
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "reports.cannotGenerateData")
      );
  };

  return (
    <>
      {openModal && (
        <SetCountryStatusModal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setActiveRecord({});
          }}
          record={activeRecord}
        />
      )}
      <TwoRow
        className={Styles["main-container"]}
        topSection={
          <ProductCategoryListingTopContainer
            query={query}
            setQuery={setQuery}
            isAdmin={authState?.currentRole?.id === userRole.ADMIN}
            downloadProductCategoryReportsHandler={
              downloadProductCategoryReportsHandler
            }
          />
        }
        bottomSection={
          <CustomTable
            locale={{
              emptyText: !productCategoryState?.productCategoryList?.result && (
                <div style={{ minHeight: variables.customTableHeight }} />
              ),
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  if (record.productCategoryId === selectedRow)
                    setSelectedRow(null);
                  else setSelectedRow(record.productCategoryId);
                },
              };
            }}
            rowClassName={(record) =>
              record?.subCategory?.some(({ isActive }) => !isActive) &&
              Styles["inactive-row"]
            }
            sticky={{ offsetHeader: 0 }}
            isMobile={responsiveArray.isSm}
            columns={[
              ...columns(intl, !responsive?.isMd),
              {
                title: <></>,
                dataIndex: "operation",
                key: "operation",
                align: "center",
                render: (_, record) => {
                  //NOSONAR
                  return (
                    <TableActions
                      openPopover={selectedRow === record.productCategoryId}
                      tableActionsOptions={tableActionsOptions(record)}
                    />
                  );
                },
              },
            ]}
            dataSource={productCategoryState?.productCategoryList?.result}
            query={query}
            setQuery={setQuery}
            totalCount={parseInt(
              productCategoryState?.productCategoryList?.totalCount
            )}
            loading={productCategoryState?.loading}
            prevText={getLocalizedMessage(intl, "pagination.prevText")}
            nextText={getLocalizedMessage(intl, "pagination.nextText")}
            sizeChangeOptions={recordsPerPageOptions.map((rec) => ({
              value: rec,
              label: (
                <Text className={Styles["select-item-style"]}>
                  {rec}&nbsp;
                  {getLocalizedMessage(intl, "pagination.selectLabelSuffix")}
                </Text>
              ),
            }))}
            tableStyleClassName={`${Styles["custom-table-style"]} ${
              Styles[!responsive?.isSm && "custom-table-mobile-style"]
            } `}
          />
        }
      />
    </>
  );
};

export default ProductCategoryListingContainer;

function getProductCategoryListParams(query) {
  let queryParams = {
    pageLimit: query.recordsPerPage,
    pageSkip: query.recordsPerPage * (query.page - 1),
  };
  if (query.searchStr.length) {
    queryParams = {
      ...queryParams,
      searchStr: encodeURI(query.searchStr),
    };
  }
  return queryParams;
}
